import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useApolloClient } from '@apollo/client';
import { booleanPointInPolygon } from '@turf/turf';
import {
  Container, Nav, NavItem, NavLink, Button,
  UncontrolledButtonDropdown, UncontrolledTooltip, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import classnames from 'classnames';
import { defaultAmenities } from '@sparklib';
import { toast } from 'react-toastify';
import { fastPropertiesFilterAtom, DEFAULT_FAST_PROPERTIES_FILTERS } from '../../../store/atoms/FastPropertiesAtom';
import { selectedPropertiesAtom, selectedPropertyArrayAtom } from '../../../store/atoms/PropertiesAtom';
import { FAST_PROPERTIES, PROPERTIES_GEOJSON, PROPERTY_BY_SLUG } from '@api';
import { buildMapVars } from './locationMapUtils';
import { Icon } from '@assets';
import FilterBar from './FilterBar';
import PropertyList from './PropertyList';
import PropertyTable from './PropertyTable';
import SidebarRight from '../../../components/SidebarRight';
import ShowProperty from '../Properties/ShowProperty';
import SendReport from '../Properties/SendReport';
import './FastProperties.css';

toast.configure();

const PAGE_SIZE = 24;

const SORT_OPTIONS = [
  { value: 'built_year_desc', label: 'Newest First' },
  { value: 'price_asc',       label: 'Price: Low → High' },
];

// propertyIds: when a drawn shape is active, pass the matching property IDs so the
// backend can filter to only those properties (requires PropertyFilterInput.propertyIds: [Int]).
function buildQueryFilters(filters, propertyIds = null) {
  const f = {};
  if (filters.search)                   f.search           = filters.search;
  // When propertyIds encodes the full spatial union client-side, skip these —
  // passing both would AND-filter and break the union.  Fall back to backend
  // filtering only when no client-side union is active (propertyIds === null).
  if (filters.neighborhoodIds?.length && propertyIds === null) f.neighborhoodIds = filters.neighborhoodIds;
  if (filters.zipCodes?.length        && propertyIds === null) f.zipCodes        = filters.zipCodes;
  if (filters.minPrice)                 f.minPrice         = filters.minPrice;
  if (filters.maxPrice)                 f.maxPrice         = filters.maxPrice;
  if (filters.bedTypes?.length)         f.bedTypes         = filters.bedTypes;
  if (filters.minBaths)                 f.minBaths         = filters.minBaths;
  if (filters.minSqft)                  f.minSqft          = filters.minSqft;
  if (filters.amenities?.length)        f.amenities        = filters.amenities;
  if (filters.excludeAmenities?.length) f.excludeAmenities = filters.excludeAmenities;
  if (filters.policies?.length)         f.policies         = filters.policies;
  if (filters.availFrom)                f.availFrom        = filters.availFrom;
  if (filters.availTo)                  f.availTo          = filters.availTo;
  if (filters.builtAfter)               f.builtAfter       = filters.builtAfter;
  if (filters.builtBefore)              f.builtBefore      = filters.builtBefore;
  if (filters.renovatedAfter)           f.renovatedAfter   = filters.renovatedAfter;
  if (filters.minEscort)                f.minEscort        = filters.minEscort;
  if (filters.minSend)                  f.minSend          = filters.minSend;
  if (filters.minBonus)                 f.minBonus         = filters.minBonus;
  if (propertyIds !== null) f.propertyIds = propertyIds.length > 0 ? propertyIds : [-1];
  f.onlyAvailable = filters.onlyAvailable !== false;
  f.sort   = filters.sort || 'built_year_desc';
  f.limit  = PAGE_SIZE;
  f.offset = filters.offset || 0;
  return f;
}

function loadView() {
  try { return localStorage.getItem('fp_view_mode') || 'list'; } catch (_) { return 'list'; }
}

export default function FastProperties({ basePath = '/fast-properties', ...props }) {
  const history = useHistory();
  const { slug, city_name, neighborhood_name } = useParams();

  const [filters, setFilters] = useRecoilState(fastPropertiesFilterAtom);
  const apolloClient = useApolloClient();

  // Seed filters from `location.state` when navigating here from the Client popover's
  // "Find and Send Recommendations Now" button (see ClientList.searchMatchingProperties).
  // That producer uses the OLD array-of-{name,label,value} shape; translate to the
  // flat FastProperties atom shape, then clear state so a refresh doesn't re-apply it.
  useEffect(() => {
    const incoming = props.location?.state;
    if (!Array.isArray(incoming)) return;

    const byName = Object.fromEntries(incoming.map((f) => [f.name, f.value]));
    const parseJSON = (s) => { try { return JSON.parse(s); } catch (_) { return {}; } };
    const city = parseJSON(byName.city);
    const bath = parseJSON(byName.bath);

    setFilters((prev) => ({
      ...prev,
      cityId:          city.value ? Number(city.value) : prev.cityId,
      search:          byName.search || '',
      neighborhoodIds: Array.isArray(byName.neighborhoods)
        ? byName.neighborhoods.map((o) => Number(o.value)).filter(Boolean)
        : [],
      zipCodes:        [],
      minPrice:        byName.minPrice || null,
      maxPrice:        byName.maxPrice || null,
      bedTypes:        Array.isArray(byName.bed) ? byName.bed : [],
      minBaths:        bath.value ? parseFloat(bath.value) : null,
      minSqft:         byName.sqFt ? parseInt(byName.sqFt, 10) : null,
      availFrom:       byName.availFrom || null,
      availTo:         byName.availTo   || null,
      builtAfter:      byName.yearFrom  || null,
      builtBefore:     byName.yearTo    || null,
      policies:        Array.isArray(byName.policies) ? byName.policies.map((o) => o.value) : [],
      amenities:       Array.isArray(byName.amenities)
        ? byName.amenities.flatMap((name) => {
            const entry = defaultAmenities.find((a) => a.name === name);
            return entry ? entry.search : [];
          })
        : [],
      excludeAmenities: [],
      drawnShape:      null,
      offset:          0,
    }));

    history.replace({ ...props.location, state: undefined });
  }, []);

  const [selectedProperties, setSelectedProperties] = useRecoilState(selectedPropertiesAtom);
  const [selectedPropertyArray, setSelectedPropertyArray] = useRecoilState(selectedPropertyArrayAtom);

  const [isMapExpanded, setIsMapExpanded] = useState(false);

  // Compute the union of property IDs that match any drawn shape OR any selected
  // neighborhood OR any selected zip — all client-side from the Apollo GeoJSON cache.
  // The combined set is passed as `propertyIds` to the backend, which restricts results
  // to exactly those properties. Falls back to null (no restriction) when no spatial
  // filter is active or when the GeoJSON cache isn't warm yet (backend handles it).
  const spatialPropertyIds = useMemo(() => {
    const shapes  = Array.isArray(filters.drawnShape)
      ? filters.drawnShape
      : (filters.drawnShape ? [filters.drawnShape] : []);
    const nbhdSet = filters.neighborhoodIds?.length > 0
      ? new Set(filters.neighborhoodIds.map(Number)) : null;
    const zipSet  = filters.zipCodes?.length > 0
      ? new Set(filters.zipCodes) : null;

    const hasSpatialFilter = shapes.length > 0 || nbhdSet || zipSet;
    if (!hasSpatialFilter || !filters.cityId) return null;

    try {
      const cached = apolloClient.readQuery({
        query: PROPERTIES_GEOJSON,
        variables: buildMapVars(filters),
      });
      if (!cached?.propertiesGeojson) return null;
      const geoData = typeof cached.propertiesGeojson === 'string'
        ? JSON.parse(cached.propertiesGeojson)
        : cached.propertiesGeojson;
      const features = geoData?.features || [];
      // Union: include property if inside ANY shape OR in ANY selected neighborhood OR zip
      return features
        .filter(f => {
          if (shapes.length > 0 && shapes.some(shape => {
            try { return booleanPointInPolygon(f, shape); } catch (_) { return false; }
          })) return true;
          if (nbhdSet && nbhdSet.has(Number(f.properties?.neighborhood_id))) return true;
          if (zipSet  && zipSet.has(f.properties?.zip)) return true;
          return false;
        })
        .map(f => Number(f.properties?.id))
        .filter(Boolean);
    } catch (_) { return null; }
  }, [filters.drawnShape, filters.neighborhoodIds, filters.zipCodes, filters.cityId]);

  const [allProperties, setAllProperties] = useState([]);
  const [totalMatched, setTotalMatched] = useState(0);
  const [view, setView] = useState(loadView);
  const scrollContainerRef = useRef(null);
  const loadingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const sentinelObserverRef = useRef(null);

  // Prefetch PROPERTIES_GEOJSON (all city dots, no spatial filters) so the
  // mini-map loads instantly from cache when the user opens the Location filterbox.
  useEffect(() => {
    if (!filters.cityId) return;
    apolloClient.query({
      query: PROPERTIES_GEOJSON,
      variables: buildMapVars(filters),
      fetchPolicy: 'network-only',
    }).catch(() => {});
  }, [filters.cityId]); 

  // Sidebar state
  const [sidebarState, setSidebarState] = useState(!!slug);
  const [sidebarWidth] = useState('1150px');
  const [showInSidebar, setShowInSidebar] = useState('');
  const [clickedProperty, setClickedProperty] = useState(null);

  // Cold-load: when arriving via a slug share link (no row click to seed
  // clickedProperty), resolve the slug → property via PROPERTY_BY_SLUG so the
  // drawer can render. Skipped during normal click-through (clickedProperty
  // is already populated) and when there's no slug in the URL.
  const { data: slugData } = useQuery(PROPERTY_BY_SLUG, {
    variables: { slug },
    skip: !slug || !!clickedProperty,
  });

  useEffect(() => {
    if (!slug) return;
    if (!slugData) return;
    const resolved = slugData.propertyBySlug;
    if (!resolved) {
      // Slug didn't resolve — drop back to the list and surface why.
      // Backend returns null for unknown slugs AND for slugs the user lacks
      // access to (city not on platform, role-gated, etc.) — same UX either way.
      toast.error('Property not found.');
      history.replace(basePath);
      setSidebarState(false);
      return;
    }
    setClickedProperty(resolved);
    // Cross-city share link → switch list to the property's city and reset
    // other filters (they were chosen for the previous city). Same-city case
    // skips this branch so the existing list query isn't refetched.
    const resolvedCityId = Number(resolved.city?.id);
    if (resolvedCityId && resolvedCityId !== filters.cityId) {
      setFilters({ ...DEFAULT_FAST_PROPERTIES_FILTERS, cityId: resolvedCityId });
    }
    // Canonicalize URL if the city/neighborhood segments don't match the
    // property's actual slugs. Use replace so back-button skips the bad URL.
    const canonicalCity = resolved.city?.slug;
    const canonicalNbhd = resolved.neighborhood?.slug;
    if (canonicalCity && canonicalNbhd &&
        (city_name !== canonicalCity || neighborhood_name !== canonicalNbhd)) {
      history.replace(`${basePath}/${canonicalCity}/${canonicalNbhd}/${slug}`);
    }
  }, [slugData, slug]);

  // Column state lifted from PropertyTable
  const [columnOptions, setColumnOptions] = useState([]);
  const [visibleColumnIds, setVisibleColumnIds] = useState([]);
  const [columnToggleFn, setColumnToggleFn] = useState(null);
  const [restoreDefaultsFn, setRestoreDefaultsFn] = useState(null);

  const { loading, error } = useQuery(FAST_PROPERTIES, {
    variables: {
      cityId: filters.cityId,
      filters: buildQueryFilters(filters, spatialPropertyIds),
    },
    // Suppress all property list queries while the expanded map is open —
    // user is focused on map filtering; list is not visible. On close, the
    // query re-evaluates with the final filter state (Apollo cache-first).
    skip: !filters.cityId || isMapExpanded,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const result = data?.fastProperties;
      if (!result) return;
      if (filters.offset === 0) {
        setAllProperties(result.nodes || []);
      } else {
        setAllProperties((prev) => [...prev, ...(result.nodes || [])]);
      }
      setTotalMatched(result.totalMatched || 0);
      hasNextPageRef.current = result.hasNextPage || false;
      loadingRef.current = false;
    },
  });

  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // Callback ref for the sentinel element — creates/destroys the IntersectionObserver
  // whenever the sentinel mounts or unmounts (e.g. city change → loading state → new render).
  // This avoids the bug where the old observer watches a detached DOM node after the sentinel
  // is destroyed by the "loading && offset === 0" conditional branch.
  const sentinelRef = useCallback((node) => {
    if (sentinelObserverRef.current) {
      sentinelObserverRef.current.disconnect();
      sentinelObserverRef.current = null;
    }
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPageRef.current && !loadingRef.current) {
          loadingRef.current = true;
          setFilters(f => ({ ...f, offset: (f.offset || 0) + PAGE_SIZE }));
        }
      },
      { root: scrollContainerRef.current, rootMargin: '300px', threshold: 0 }
    );
    observer.observe(node);
    sentinelObserverRef.current = observer;
  }, []); // all dynamic values accessed via refs or stable Recoil setter — no deps needed

  // After each batch loads: if sentinel is still in/near bottom of scroll container, keep loading
  useEffect(() => {
    if (loading || !hasNextPageRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const fromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (fromBottom <= 300) {
      loadingRef.current = true;
      setFilters(f => ({ ...f, offset: (f.offset || 0) + PAGE_SIZE }));
    }
  }, [allProperties.length]);

  // ── Keyboard scroll: redirect ↑/↓ to the sidebar when it is open ──────────
  useEffect(() => {
    const sidebar = document.getElementById('sidebar-right-id');
    if (!sidebar) return;

    if (sidebarState) {
      // Make the sidebar scrollable (it has no overflow-y in the global CSS)
      sidebar.style.overflowY = 'auto';

      function handleKey(e) {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        sidebar.scrollBy({ top: e.key === 'ArrowDown' ? 120 : -120, behavior: 'smooth' });
      }
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('keydown', handleKey);
        sidebar.style.overflowY = '';
      };
    }
  }, [sidebarState]);

  // ── Active filters for client-side floor-plan display filtering ───────────
  // Passed to PropertyList/PropertyTable so each card/row shows only the floor
  // plans that match the current filter criteria.
  const activeFilters = useMemo(() => ({
    bedTypes:  filters.bedTypes  || [],
    minPrice:  filters.minPrice  || 0,
    maxPrice:  filters.maxPrice  || 0,
    minSqft:   filters.minSqft   || 0,
    availFrom: filters.availFrom || null,
    availTo:   filters.availTo   || null,
  }), [filters.bedTypes, filters.minPrice, filters.maxPrice, filters.minSqft, filters.availFrom, filters.availTo]);

  // ── Selected properties float to the top, unselected retain server order ───
  const displayProperties = useMemo(() => {
    if (!selectedProperties.length) return allProperties;
    const selectedIds = new Set(selectedProperties.map(sp => Number(sp.propertyId)));
    const selected   = allProperties.filter(p =>  selectedIds.has(Number(p.id)));
    const unselected = allProperties.filter(p => !selectedIds.has(Number(p.id)));
    return [...selected, ...unselected];
  }, [allProperties, selectedProperties]);

  // ── Sync selectedPropertyArray when selectedProperties changes ─────────────
  // TabList (inside ShowProperty) only calls setSelectedProperties when a
  // floor plan / unit is selected; this effect keeps selectedPropertyArray in
  // sync so the send-report list stays accurate.
  useEffect(() => {
    const uniqueIds = [...new Set(selectedProperties.map(sp => Number(sp.propertyId)))];
    setSelectedPropertyArray(prev =>
      uniqueIds.map(propertyId => {
        const existing = prev.find(p => Number(p.node.id) === propertyId);
        if (existing) return existing;
        const found = allProperties.find(p => Number(p.id) === propertyId);
        return found ? { node: { ...found, highlight: true } } : { node: { id: String(propertyId) } };
      })
    );
  }, [selectedProperties]);

  // ── Selected properties ────────────────────────────────────────────────────

  function toggleSelection(property) {
    const propertyId = Number(property.id);
    const isSelected = selectedProperties.some(p => Number(p.propertyId) === propertyId);
    if (isSelected) {
      setSelectedProperties(prev => prev.filter(p => Number(p.propertyId) !== propertyId));
      setSelectedPropertyArray(prev => prev.filter(p => Number(p.node.id) !== propertyId));
    } else {
      setSelectedProperties(prev => [...prev, { propertyId, floorPlanId: null, typeDetailId: null }]);
      setSelectedPropertyArray(prev => [...prev, { node: { ...property, highlight: true } }]);
    }
  }

  // ── Sidebar ────────────────────────────────────────────────────────────────

  function onRowClick(property) {
    setClickedProperty(property);
    setSidebarState(true);
    const citySlug = property?.city?.slug;
    const nbhdSlug = property?.neighborhood?.slug;
    const propSlug = property?.slug;
    if (citySlug && nbhdSlug && propSlug) {
      history.push(`${basePath}/${citySlug}/${nbhdSlug}/${propSlug}`);
    } else {
      history.push(basePath);
    }
  }

  const showProperty = (
    <ShowProperty
      property={null}
      queryVariables={{}}
      initialData={clickedProperty}
      selectedProperties={selectedProperties}
      setSelectedProperties={setSelectedProperties}
      sidebarToggle={() => setSidebarState(s => !s)}
      activeFilters={activeFilters}
      {...props}
    />
  );

  const showSendList = (
    <SendReport
      selectedCityId={filters.cityId}
      selectedProperties={selectedProperties}
      selectedPropertyArray={selectedPropertyArray}
      removePropertySelection={(p) => {
        const propertyId = Number(p.node?.id || p.propertyId);
        setSelectedProperties(prev => prev.filter(sp => Number(sp.propertyId) !== propertyId));
        setSelectedPropertyArray(prev => prev.filter(sp => Number(sp.node.id) !== propertyId));
      }}
      sidebarToggle={() => setSidebarState(s => !s)}
      sidebarState={sidebarState}
      {...props}
    />
  );

  useEffect(() => {
    if (sidebarState) {
      if (slug) {
        setShowInSidebar(showProperty);
      } else {
        setShowInSidebar(showSendList);
        history.push(basePath);
      }
    } else {
      setShowInSidebar('');
      setClickedProperty(null);
      history.push(basePath);
    }
  }, [sidebarState, slug, selectedPropertyArray, clickedProperty]);

  // ── View / sort ────────────────────────────────────────────────────────────

  function switchView(v) {
    setView(v);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    try { localStorage.setItem('fp_view_mode', v); } catch (_) {}
  }

  // Force card view on phone widths without overwriting the user's saved preference.
  // setView (not switchView) keeps localStorage untouched, so resizing back to
  // tablet/desktop restores their last-chosen view.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-width: 767.98px)');
    const apply = () => { if (mql.matches && view === 'list') setView('cards'); };
    apply();
    const handler = () => apply();
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [view]);

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === filters.sort)?.label || 'Newest First';
  const isDefaultSort = (filters.sort || 'built_year_desc') === 'built_year_desc';

  const selectButtonLabel = (() => {
    switch (selectedPropertyArray.length) {
      case 0:  return 'Select Properties';
      case 1:  return 'Send 1 Property';
      default: return `Send ${selectedPropertyArray.length} Properties`;
    }
  })();

  return (
    <>
      <SidebarRight
        sidebarState={sidebarState}
        sidebarToggle={() => setSidebarState(s => !s)}
        sidebarContent={showInSidebar}
        sidebarWidth={sidebarWidth}
        sidebarButton={false}
      />

      <div className='fp-page'>
        <FilterBar filters={filters} setFilters={setFilters} onMapExpand={setIsMapExpanded} totalMatched={totalMatched} />

        <Container fluid className='fp-main-content'>
          {error && (
            <div className='fp-error'>Failed to load properties. Please try again.</div>
          )}

          {/* ── Toolbar ──────────────────────────────────────────────────────── */}
          <div className='fp-toolbar-wrapper'>
            <div className='table-tabs lg pt-2'>
            <Nav tabs className='align-items-center fp-toolbar-nav'>
              {/* View switcher — List hidden on phone (cards forced) */}
              <NavItem className='d-none d-md-block'>
                <NavLink
                  id='fp-view-list-tab'
                  className={classnames('fp-view-tab', { active: view === 'list' })}
                  onClick={() => switchView('list')}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon icon='list' />
                  <span className='fp-tab-label ml-2'>List</span>
                </NavLink>
                <UncontrolledTooltip target='fp-view-list-tab' placement='bottom'>List view</UncontrolledTooltip>
              </NavItem>
              <NavItem className='d-none d-md-block' style={{ borderTopRightRadius: 'var(--rounded-big)', overflow: 'hidden' }}>
                <NavLink
                  id='fp-view-cards-tab'
                  className={classnames('fp-view-tab', { active: view === 'cards' })}
                  onClick={() => switchView('cards')}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon icon='square' />
                  <span className='fp-tab-label ml-2'>Cards</span>
                </NavLink>
                <UncontrolledTooltip target='fp-view-cards-tab' placement='bottom'>Card view</UncontrolledTooltip>
              </NavItem>

              {/* Send N Properties + Clear — only when properties are selected */}
              {selectedPropertyArray.length > 0 && (
                <div className='w-auto d-flex align-items-center fp-send-block'>
                  <Button
                    className='button-dashboard btn-blue'
                    onClick={() => setSidebarState(s => !s)}
                  >
                    <Icon icon='paper-plane' />{' '}
                    <span className='fp-send-label-full'>{selectButtonLabel}</span>
                    <span className='fp-send-label-short'>Send {selectedPropertyArray.length}</span>
                  </Button>
                  <span
                    id='fp-clear-selections'
                    className='ml-2 text-primary fp-clear-selections'
                    onClick={() => {
                      setSelectedProperties([]);
                      setSelectedPropertyArray([]);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className='fp-clear-label'>Clear</span>
                    <Icon icon='times' className='fp-clear-icon' />
                  </span>
                  <UncontrolledTooltip target='fp-clear-selections' placement='bottom'>Clear selections</UncontrolledTooltip>
                </div>
              )}

              {/* Result count */}
              {totalMatched > 0 && (
                <div className='fp-result-count' style={{ fontSize: 15, fontWeight: 500, color: 'var(--gray-700)' }}>
                  {totalMatched.toLocaleString()} propert{totalMatched === 1 ? 'y' : 'ies'}<span className='d-none d-sm-inline'> found</span>
                </div>
              )}

              {/* Right side controls */}
              <div className='ml-auto d-flex align-items-center' style={{ gap: 6 }}>
                {/* Sort */}
                <UncontrolledButtonDropdown>
                  <DropdownToggle
                    caret
                    id='fp-sort-toggle'
                    className={classnames('btn button-dashboard btn-blue fp-icon-toggle', { 'fp-has-active': !isDefaultSort })}
                  >
                    <Icon icon='sort' className='fp-sort-icon' />
                    <span className='fp-control-label'>{currentSortLabel}</span>
                  </DropdownToggle>
                  <UncontrolledTooltip target='fp-sort-toggle' placement='bottom'>Sort by</UncontrolledTooltip>
                  <DropdownMenu right>
                    {SORT_OPTIONS.map(opt => (
                      <DropdownItem
                        key={opt.value}
                        onClick={() => setFilters(f => ({ ...f, sort: opt.value, offset: 0 }))}
                      >
                        {filters.sort === opt.value && <Icon icon='check' className='mr-2' />}
                        {opt.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledButtonDropdown>

                {/* Columns — only in list view */}
                {view === 'list' && columnOptions.length > 0 && (
                  <UncontrolledButtonDropdown>
                    <DropdownToggle
                      caret
                      id='fp-columns-toggle'
                      className='btn button-dashboard btn-blue fp-icon-toggle'
                    >
                      <Icon icon='columns' />
                      <span className='fp-control-label ml-1'>Columns</span>
                    </DropdownToggle>
                    <UncontrolledTooltip target='fp-columns-toggle' placement='bottom'>Columns</UncontrolledTooltip>
                    <DropdownMenu right>
                      <DropdownItem
                        onClick={() => restoreDefaultsFn && restoreDefaultsFn()}
                        style={{ fontWeight: 600, color: 'var(--theme-blue, #2980b9)' }}
                      >
                        <Icon icon='undo' className='mr-2' />Restore Defaults
                      </DropdownItem>
                      <DropdownItem divider />
                      {columnOptions.map((opt, i) => (
                        <DropdownItem
                          key={i}
                          disabled={opt.value === 'name'}
                          onClick={() => opt.value !== 'name' && columnToggleFn && columnToggleFn(opt)}
                        >
                          {visibleColumnIds.includes(opt.value)
                            ? <Icon icon='check' className='mr-2' />
                            : <Icon icon='check' className='mr-2 hidden' />}
                          {opt.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                )}
              </div>
            </Nav>
            </div>{/* .table-tabs */}
          </div>{/* .fp-toolbar-wrapper */}

          {/* ── Scrollable content area ───────────────────────────────────────── */}
          <div className='fp-scroll-area' ref={scrollContainerRef}>
            {loading && filters.offset === 0 ? (
              <div className='fp-initial-loading'>Loading properties...</div>
            ) : view === 'cards' ? (
              <div className='fp-cards-container'>
                <PropertyList
                  properties={displayProperties}
                  loading={loading}
                  sentinelRef={sentinelRef}
                  selectedProperties={selectedProperties}
                  toggleSelection={toggleSelection}
                  onRowClick={onRowClick}
                  activeFilters={activeFilters}
                />
              </div>
            ) : (
              <div className='apartment-table'>
                <PropertyTable
                  properties={displayProperties}
                  loading={loading}
                  sentinelRef={sentinelRef}
                  selectedProperties={selectedProperties}
                  toggleSelection={toggleSelection}
                  onRowClick={onRowClick}
                  activeFilters={activeFilters}
                  onColumnsReady={(opts, visible, toggleFn, restoreFn) => {
                    setColumnOptions(opts);
                    setVisibleColumnIds(visible);
                    setColumnToggleFn(() => (opt) => {
                      toggleFn(opt);
                      setVisibleColumnIds(prev =>
                        prev.includes(opt.value)
                          ? prev.filter(id => id !== opt.value)
                          : [...prev, opt.value]
                      );
                    });
                    setRestoreDefaultsFn(() => restoreFn);
                  }}
                />
              </div>
            )}
          </div>{/* .fp-scroll-area */}
        </Container>
      </div>
    </>
  );
}
